/**
 * The first screen, and it is two different screens.
 *
 * For somebody who has just created a workspace it is the question the brief asks
 * for — "What would you like to do?" — with three answers and nothing else on the
 * page. No counters at zero, no empty charts, no "welcome to your dashboard". A new
 * user has nothing to manage and giving them management chrome is how a product
 * feels like work before it has done any.
 *
 * For somebody who has used it, it is the shortest path back to the thing they were
 * doing: the design they edited last, the data they brought in last, and the
 * document they made last. Three cards, each a continuation rather than a summary.
 */
import { useEffect, useState } from "react";
import { api, type DatasetSummary, type ReportSummary, type TemplateSummary } from "../api.js";
import { Bar, Card, Chip, Icon, Note, Skeleton, Thumb, bytes, count, when } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";
import { useChanges } from "../changes.js";

export function Home() {
  const { view } = useWorkspace();
  // Something renamed or deleted from the sidebar is renamed or gone here too.
  const changes = useChanges();
  const [state, setState] = useState<{ datasets: DatasetSummary[]; templates: TemplateSummary[]; reports: ReportSummary[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.datasets(), api.templates(), api.reports()])
      .then(([d, t, r]) => !cancelled && setState({ datasets: d.datasets, templates: t.templates, reports: r.reports }))
      .catch(() => !cancelled && setState({ datasets: [], templates: [], reports: [] }));
    return () => {
      cancelled = true;
    };
  }, [changes]);

  if (!state) {
    return (
      <div className="wrap stack" style={{ gap: 20 }}>
        <div className="skeleton" style={{ height: 44, width: 360 }} />
        <Skeleton rows={3} height={120} />
      </div>
    );
  }

  const fresh = state.datasets.length === 0 && state.templates.length === 0;

  /* ------------------------------ the first visit ------------------------- */

  if (fresh) {
    return (
      <div className="wrap--narrow stack" style={{ gap: 28, paddingTop: "clamp(8px, 4vh, 40px)" }}>
        <div className="stack" style={{ gap: 10 }}>
          <span className="eyebrow">{view.workspace.name}</span>
          <h1>What would you like to do?</h1>
          <p className="head__lede" style={{ fontSize: 17 }}>
            Two minutes to your first document. Start wherever suits — you can do the other part afterwards.
          </p>
        </div>

        <div className="stack" style={{ gap: 12 }}>
          <a className="tile" href="/data/new" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Icon name="upload" className="drop__icon" />
            <div className="stack" style={{ gap: 4 }}>
              <strong style={{ fontSize: 17 }}>Bring in my data</strong>
              <span className="muted">
                Drag in a spreadsheet, or paste rows out of Excel. BroadPaper works out the columns and the formats, then you pick a design.
              </span>
            </div>
            <Icon name="chevron" />
          </a>

          <a className="tile" href="/designs" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Icon name="design" className="drop__icon" />
            <div className="stack" style={{ gap: 4 }}>
              <strong style={{ fontSize: 17 }}>Start from a finished document</strong>
              <span className="muted">
                An invoice, a quote, a statement, a monthly report, a client report or a certificate. Each one comes with example figures, so you can see
                what you are getting.
              </span>
            </div>
            <Icon name="chevron" />
          </a>

          <a className="tile" href="/designs" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <Icon name="document" className="drop__icon" />
            <div className="stack" style={{ gap: 4 }}>
              <strong style={{ fontSize: 17 }}>Build a page from scratch</strong>
              <span className="muted">Your own layout, with your own columns. Drag a list onto the page and it becomes a table.</span>
            </div>
            <Icon name="chevron" />
          </a>
        </div>

        <Note tone="info" title="Nothing to set up">
          Your plan is {view.plan.name}. {view.plan.marked ? "Documents carry a small BroadPaper line at the foot of each page — everything else works exactly as it will when you pay." : "Documents carry no BroadPaper mark."}
        </Note>
      </div>
    );
  }

  /* ------------------------------- coming back ---------------------------- */

  const design = state.templates[0];
  const dataset = state.datasets[0];
  const report = state.reports[0];

  return (
    <div className="wrap stack" style={{ gap: 24 }}>
      <div className="head">
        <div className="head__title">
          <span className="eyebrow">{view.workspace.name}</span>
          <h1>Pick up where you left off</h1>
        </div>
        <div className="head__actions">
          <a className="btn" href="/data/new">
            <Icon name="upload" /> Bring in data
          </a>
          <a className="btn btn--primary" href="/designs">
            <Icon name="plus" /> New design
          </a>
        </div>
      </div>

      <div className="grid grid--3">
        {design ? (
          <a className="tile tile--thumb" href={`/designs/${design.id}`}>
            <Thumb thumbnail={design.thumbnail} label={design.name} size="sm" />
            <div className="stack" style={{ gap: 8, minWidth: 0 }}>
              <span className="eyebrow">Last design</span>
              <strong style={{ fontSize: 16 }}>{design.name}</strong>
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                {design.dataset ? <Chip tone="good">{design.dataset.name}</Chip> : <Chip tone="ask">Needs data</Chip>}
              </div>
              <span className="small muted">Edited {when(design.updatedAt)}</span>
            </div>
          </a>
        ) : (
          <a className="tile" href="/designs">
            <div className="stack" style={{ gap: 8 }}>
              <span className="eyebrow">Designs</span>
              <strong style={{ fontSize: 16 }}>Pick something to start from</strong>
              <span className="small muted">Invoices, quotes, receipts and more, finished — or an empty page.</span>
            </div>
          </a>
        )}

        {dataset ? (
          <a className="tile" href={`/data/${dataset.id}`}>
            <div className="stack" style={{ gap: 8 }}>
              <span className="eyebrow">Last data</span>
              <strong style={{ fontSize: 16 }}>{dataset.name}</strong>
              <span className="small muted num">
                {count(dataset.rowCount)} rows · {dataset.columns.length} columns
              </span>
              <span className="small muted">Brought in {when(dataset.createdAt)}</span>
            </div>
          </a>
        ) : (
          <a className="tile" href="/data/new">
            <div className="stack" style={{ gap: 8 }}>
              <span className="eyebrow">Your data</span>
              <strong style={{ fontSize: 16 }}>Bring in a spreadsheet</strong>
              <span className="small muted">Your designs will fill themselves in from it.</span>
            </div>
          </a>
        )}

        {report ? (
          <a className="tile" href="/documents">
            <div className="stack" style={{ gap: 8 }}>
              <span className="eyebrow">Last document</span>
              <strong style={{ fontSize: 16 }}>{report.name.replace(/\.pdf$/, "")}</strong>
              <span className="small muted">
                {report.status === "ready" ? `${report.pages} page${report.pages === 1 ? "" : "s"}` : report.status === "failed" ? "Could not be produced" : "Being produced…"}
              </span>
              <span className="small muted">{when(report.createdAt)}</span>
            </div>
          </a>
        ) : (
          <div className="tile" style={{ cursor: "default" }}>
            <div className="stack" style={{ gap: 8 }}>
              <span className="eyebrow">Documents</span>
              <strong style={{ fontSize: 16 }}>Nothing produced yet</strong>
              <span className="small muted">Open a design and press “Make the document”.</span>
            </div>
          </div>
        )}

        {/* The letterhead. Fourth tile rather than first, because the order here is
            the order of the work — but on it, because a document with a blank where
            the business's name belongs is the first thing anybody notices and the
            last thing they would think to go looking for. */}
        <a className="tile" href="/brand">
          <div className="stack" style={{ gap: 8 }}>
            <span className="eyebrow">Your brand</span>
            <strong style={{ fontSize: 16 }}>How your documents are headed</strong>
            <span className="small muted">Your name, address and logo, on everything you make.</span>
          </div>
        </a>
      </div>

      {/*
        The counters, and only now that there is something to count. A usage panel
        in front of somebody on their first visit is chrome; in front of somebody
        with nine designs it is information.
      */}
      <Card flat>
        <div className="card__head">
          <strong>This month</strong>
          <a className="small" href="/account">
            Your plan: {view.plan.name}
          </a>
        </div>
        <div className="card__body grid grid--3">
          <Usage label="Documents made" used={view.usage.reportsThisMonth} total={view.limits.reportsPerMonth} format={count} />
          <Usage label="Sets of data" used={view.usage.datasets} total={view.limits.datasets} format={count} />
          <Usage label="Storage used" used={view.usage.storageBytes} total={view.limits.storageBytes} format={bytes} />
        </div>
      </Card>
    </div>
  );
}

function Usage({ label, used, total, format }: { label: string; used: number; total: number; format: (n: number) => string }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="small muted">{label}</span>
      <strong className="num" style={{ fontSize: 17 }}>
        {format(used)} <span className="muted" style={{ fontWeight: 400, fontSize: 14 }}>of {format(total)}</span>
      </strong>
      <Bar used={used} total={total} />
    </div>
  );
}
